import axios from 'axios';
import { 
    API_BASE_URL, 
    API_CIMA_SEARCH, 
    API_CIMA_DETALLE, 
    API_CIMA_ALL,
    API_CIMA_PRESENTACIONES,
    API_CIMA_PRESENTACION,
    API_CIMA_SUMINISTRO,
    API_CIMA_VMPP,
    API_CIMA_NOTAS,
    API_CIMA_MATERIALES
} from '../../../helpers/urlHelper';

const api = axios.create({ 
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: {
        'Content-Type': 'application/json',
    }
});

// Add request interceptor for logging
api.interceptors.request.use(
    (config) => {
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        if (config.params) {
            console.log('Request params:', config.params);
        }
        return config;
    },
    (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for error handling
api.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        console.error('API Response Error:', error.response?.data || error.message);
        
        if (error.code === 'ECONNABORTED') {
            throw new Error('La consulta ha tardado demasiado. Inténtalo de nuevo.');
        }
        
        if (error.response?.status === 404) {
            throw new Error('Medicamento no encontrado.');
        }
        
        if (error.response?.status === 400) {
            throw new Error(error.response?.data?.error || 'Parámetros de búsqueda inválidos.');
        }
        
        if (error.response?.status >= 500) {
            throw new Error('Error del servidor. Inténtalo más tarde.');
        }
        
        if (!error.response) {
            throw new Error('Error de conexión. Verifica tu conexión a internet.');
        }
        
        throw error;
    }
);

// ========================================================================
// MEDICAMENTOS
// ========================================================================

/**
 * Fetch all medications with pagination
 * @param {number} pagina - Page number (default: 1)
 * @param {Object} filters - Optional filters
 * @returns {Promise<Array>} Array of medications
 */
export function fetchAllMedications(pagina = 1, filters = {}) {
    const params = {
        pagina,
        ...filters
    };

    return api
        .get(API_CIMA_SEARCH, { params })
        .then(res => {
            const data = res.data;
            
            if (Array.isArray(data)) {
                return data;
            }
            
            console.warn('Unexpected response format for all medications:', data);
            return [];
        })
        .catch(error => {
            console.error('Error fetching all medications:', error);
            throw new Error('Error al cargar medicamentos. Inténtalo de nuevo.');
        });
}

/**
 * Search medications with various filters
 * @param {Object} filters - Search filters
 * @param {string} filters.nombre - Medication name
 * @param {string} filters.laboratorio - Laboratory name
 * @param {string} filters.nregistro - Registration number
 * @param {string} filters.cn - National code
 * @param {string} filters.triangulo - Triangle symbol (1/0)
 * @param {string} filters.huerfano - Orphan drug (1/0)
 * @param {string} filters.biosimilar - Biosimilar (1/0)
 * @param {string} filters.comerc - Commercial (1/0)
 * @param {string} filters.receta - Prescription required (1/0)
 * @param {number} filters.pagina - Page number
 * @returns {Promise<Array>} Array of medication suggestions
 */
export function fetchCimaSuggestions(filters = {}) {
    // Si hay un query de nombre simple, usar ese parámetro
    if (typeof filters === 'string') {
        filters = { nombre: filters };
    }
    
    const params = { ...filters };
    
    // Si no hay filtros, obtener medicamentos de la primera página
    if (Object.keys(params).length === 0) {
        params.pagina = 1;
    }
    
    return api
        .get(API_CIMA_SEARCH, { params })
        .then(res => {
            const data = res.data;
            
            if (Array.isArray(data)) {
                return data;
            }
            
            console.warn('Unexpected response format for suggestions:', data);
            return [];
        })
        .catch(error => {
            console.error('Error fetching CIMA suggestions:', error);
            
            if (error.message) {
                throw error;
            }
            
            throw new Error('Error al buscar medicamentos. Inténtalo de nuevo.');
        });
}

/**
 * Fetch detailed medication information
 * @param {string} nregistro - Medication registration number
 * @param {string} cn - National code (alternative to nregistro)
 * @returns {Promise<Object|Array>} Detailed medication information
 */
export function fetchCimaDetalle(nregistro, cn = null) {
    if (!nregistro && !cn) {
        return Promise.reject(new Error('Se requiere nregistro o cn'));
    }
    
    const params = {};
    if (nregistro) params.nregistro = nregistro;
    if (cn) params.cn = cn;
    
    return api
        .get(API_CIMA_DETALLE, { params })
        .then(res => {
            const data = res.data;
            
            if (!data) {
                throw new Error('No se encontraron detalles del medicamento');
            }
            
            return data;
        })
        .catch(error => {
            console.error('Error fetching CIMA details:', error);
            
            if (error.response?.status === 404) {
                throw new Error('Medicamento no encontrado en la base de datos');
            }
            
            if (error.message) {
                throw error;
            }
            
            throw new Error('Error al obtener detalles del medicamento');
        });
}

// ========================================================================
// PRESENTACIONES
// ========================================================================

/**
 * Fetch presentations with filters and pagination
 * @param {Object} filters - Search filters
 * @param {string} filters.cn - National code
 * @param {string} filters.nregistro - Registration number
 * @param {string} filters.comerc - Commercial (1/0)
 * @param {string} filters.estupefaciente - Narcotic (1/0)
 * @param {string} filters.psicotropo - Psychotropic (1/0)
 * @param {string} filters.estuopsico - Narcotic/Psychotropic (1/0)
 * @param {number} filters.pagina - Page number
 * @returns {Promise<Array>} Array of presentations
 */
export function fetchPresentaciones(filters = {}) {
    const params = { 
        pagina: 1,
        ...filters 
    };
    
    return api
        .get(API_CIMA_PRESENTACIONES, { params })
        .then(res => {
            const data = res.data;
            
            if (Array.isArray(data)) {
                return data;
            }
            
            console.warn('Unexpected response format for presentations:', data);
            return [];
        })
        .catch(error => {
            console.error('Error fetching presentations:', error);
            throw new Error('Error al obtener presentaciones.');
        });
}

/**
 * Fetch single presentation by national code
 * @param {string} codNacional - National code
 * @returns {Promise<Object>} Presentation details
 */
export function fetchPresentacionDetalle(codNacional) {
    if (!codNacional) {
        return Promise.reject(new Error('Código nacional requerido'));
    }
    
    return api
        .get(`${API_CIMA_PRESENTACION}/${codNacional}`)
        .then(res => {
            const data = res.data;
            
            if (!data) {
                throw new Error('Presentación no encontrada');
            }
            
            return data;
        })
        .catch(error => {
            console.error('Error fetching presentation details:', error);
            throw new Error('Error al obtener detalles de la presentación');
        });
}

// ========================================================================
// PROBLEMAS DE SUMINISTRO
// ========================================================================

/**
 * Fetch supply problems
 * @returns {Promise<Array>} Array of supply problems
 */
export function fetchProblemasSuministro() {
    return api
        .get(API_CIMA_SUMINISTRO)
        .then(res => {
            const data = res.data;
            return Array.isArray(data) ? data : [];
        })
        .catch(error => {
            console.error('Error fetching supply problems:', error);
            throw new Error('Error al obtener problemas de suministro');
        });
}

/**
 * Fetch supply problems by national code
 * @param {string} codNacional - National code
 * @returns {Promise<Array>} Array of supply problems
 */
export function fetchProblemasSuministroPorCN(codNacional) {
    if (!codNacional) {
        return Promise.reject(new Error('Código nacional requerido'));
    }
    
    return api
        .get(`${API_CIMA_SUMINISTRO}/${codNacional}`)
        .then(res => {
            const data = res.data;
            return Array.isArray(data) ? data : [];
        })
        .catch(error => {
            console.error('Error fetching supply problems by CN:', error);
            throw new Error('Error al obtener problemas de suministro');
        });
}

// ========================================================================
// VMPP (Virtual Medicinal Product Package)
// ========================================================================

/**
 * Fetch VMPP information
 * @param {Object} filters - VMPP filters
 * @param {string} filters.practiv1 - Active principle 1
 * @param {string} filters.idpractiv1 - Active principle ID 1
 * @param {string} filters.dosis - Dose
 * @param {string} filters.forma - Pharmaceutical form
 * @param {string} filters.atc - ATC code
 * @param {string} filters.nombre - Name
 * @param {string} filters.modoArbol - Tree mode
 * @returns {Promise<Array>} Array of VMPP data
 */
export function fetchVMPP(filters = {}) {
    const params = { ...filters };
    
    return api
        .get(API_CIMA_VMPP, { params })
        .then(res => {
            const data = res.data;
            return Array.isArray(data) ? data : [];
        })
        .catch(error => {
            console.error('Error fetching VMPP:', error);
            throw new Error('Error al obtener información VMPP');
        });
}

// ========================================================================
// NOTAS DE SEGURIDAD
// ========================================================================

/**
 * Fetch security notes for a medication
 * @param {string} nregistro - Registration number
 * @returns {Promise<Array>} Array of security notes
 */
export function fetchNotasSeguridadPorMedicamento(nregistro) {
    if (!nregistro) {
        return Promise.reject(new Error('Número de registro requerido'));
    }
    
    return api
        .get(`${API_CIMA_NOTAS}/${nregistro}`)
        .then(res => {
            const data = res.data;
            return Array.isArray(data) ? data : [];
        })
        .catch(error => {
            console.error('Error fetching security notes:', error);
            throw new Error('Error al obtener notas de seguridad');
        });
}

// ========================================================================
// MATERIALES INFORMATIVOS
// ========================================================================

/**
 * Fetch informative materials for a medication
 * @param {string} nregistro - Registration number
 * @returns {Promise<Array>} Array of materials
 */
export function fetchMaterialesPorMedicamento(nregistro) {
    if (!nregistro) {
        return Promise.reject(new Error('Número de registro requerido'));
    }
    
    return api
        .get(`${API_CIMA_MATERIALES}/${nregistro}`)
        .then(res => {
            const data = res.data;
            return Array.isArray(data) ? data : [];
        })
        .catch(error => {
            console.error('Error fetching materials:', error);
            throw new Error('Error al obtener materiales informativos');
        });
}

// ========================================================================
// FUNCIONES DE UTILIDAD Y BÚSQUEDA AVANZADA
// ========================================================================

/**
 * Fetch multiple medication details in batch
 * @param {Array<string>} registrationNumbers - Array of registration numbers
 * @returns {Promise<Array>} Array of detailed medication information
 */
export async function fetchCimaDetallesBatch(registrationNumbers) {
    if (!Array.isArray(registrationNumbers) || registrationNumbers.length === 0) {
        return [];
    }
    
    try {
        const promises = registrationNumbers.map(numero => 
            fetchCimaDetalle(numero).catch(error => {
                console.warn(`Failed to fetch details for ${numero}:`, error.message);
                return null;
            })
        );
        
        const results = await Promise.all(promises);
        return results.filter(result => result !== null);
    } catch (error) {
        console.error('Error in batch fetch:', error);
        throw new Error('Error al obtener detalles de medicamentos');
    }
}

/**
 * Search medications by category/filter presets
 * @param {string} category - Category name
 * @returns {Promise<Array>} Array of medications in category
 */
export function fetchMedicationsByCategory(category) {
    const categoryFilters = {
        'analgesicos': { nombre: 'paracetamol' },
        'antibioticos': { nombre: 'amoxicilina' },
        'antiinflamatorios': { nombre: 'ibuprofeno' },
        'triangulo': { triangulo: '1' },
        'huerfanos': { huerfano: '1' },
        'biosimilares': { biosimilar: '1' },
        'comercializados': { comerc: '1' },
        'receta': { receta: '1' }
    };
    
    const filters = categoryFilters[category.toLowerCase()] || {};
    
    return fetchCimaSuggestions(filters)
        .catch(error => {
            console.error(`Error fetching ${category}:`, error);
            return [];
        });
}

/**
 * Advanced search with multiple filters
 * @param {Object} filters - Advanced search filters
 * @returns {Promise<Array>} Filtered medication results
 */
export function fetchCimaAdvancedSearch(filters) {
    const cleanFilters = {};
    
    // Limpiar y validar filtros
    Object.keys(filters).forEach(key => {
        const value = filters[key];
        if (value !== null && value !== undefined && value !== '') {
            if (typeof value === 'string') {
                cleanFilters[key] = value.trim();
            } else {
                cleanFilters[key] = value;
            }
        }
    });
    
    if (Object.keys(cleanFilters).length === 0) {
        return Promise.resolve([]);
    }
    
    return fetchCimaSuggestions(cleanFilters)
        .catch(error => {
            console.error('Error in advanced search:', error);
            throw new Error('Error en la búsqueda avanzada');
        });
}